import { useEffect } from 'react'
import { storage } from '../../utils/storage'
import galleryCache from '../../services/galleryCache'
import { createLogger } from '../../utils/logger'

const logger = createLogger('useEntityDetailEffects')

/**
 * useEntityDetailEffects - Consolidated effects for EntityDetailMap
 * 
 * Handles:
 * 1. Initial channel ID synchronization
 * 2. Hash-based tab navigation (OAuth redirects)
 * 3. showUserLocation persistence
 * 4. Entity gallery fetching with cache
 * 
 * @param {Object} params - Effect parameters
 */
export function useEntityDetailEffects({
  // Channel ID sync
  initialChannelIdProp,
  setInitialChannelId,
  
  // Hash navigation
  onTabChange,
  isImmersiveMode,
  
  // Storage persistence
  showUserLocation,
  
  // Gallery fetching
  entityId,
  setEntityGallery,
  setGalleryRenderKey
}) {
  // Sync initialChannelId prop to state
  useEffect(() => {
    if (initialChannelIdProp) {
      setInitialChannelId(initialChannelIdProp)
    }
  }, [initialChannelIdProp, setInitialChannelId])

  // Handle hash-based tab navigation (e.g., #login after Google OAuth)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if ((hash === 'auth' || hash === 'login') && onTabChange && isImmersiveMode) {
      onTabChange('auth')
      // Clean up the hash from URL
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }, [onTabChange, isImmersiveMode])

  // Persist showUserLocation state to storage
  useEffect(() => {
    storage.set('showUserLocation', showUserLocation)
  }, [showUserLocation])

  // Fetch entity gallery when in entity mode (using cache service)
  useEffect(() => {
    const fetchGallery = async () => {
      if (entityId) {
        try {
          // Use gallery cache service - automatically handles rate limiting and deduplication
          const images = await galleryCache.getGallery(entityId)
          setEntityGallery([...images])
          setGalleryRenderKey(prev => prev + 1)
        } catch (error) {
          logger.error('Failed to fetch entity gallery', error)
        }
      }
    }

    if (entityId) {
      fetchGallery()
    }
  }, [entityId, setEntityGallery, setGalleryRenderKey])
}

/**
 * useShowUserLocationPersistence - Simple effect to persist showUserLocation state
 * Used by both HomePageMap and EntityDetailMap
 * 
 * @param {boolean} showUserLocation - Current showUserLocation state
 */
export function useShowUserLocationPersistence(showUserLocation) {
  useEffect(() => {
    storage.set('showUserLocation', showUserLocation)
  }, [showUserLocation])
}
